import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  UseDisclosureReturn,
} from "@chakra-ui/react";
import * as Sentry from "@sentry/browser";
import React, { useEffect } from "react";

import { Button, ExpandableTextArea } from "../../../../components";
import { TaskDefinitionType, useTaskDefinitionsQuery } from "../../../graphql";
import useFeatureFlag from "../../../graphql/hooks/useFeatureFlag";
import { useUrlState } from "../../../pages/home/useTabUrlState";
import CandidateCustomPromptResult from "./CandidateCustomPromptResult";
import useCompleteTask from "./useCompleteTask";

type CandidateCustomPromptModalProps = {
  candidateId: string;
  positionId: string | null;
  disclosure: UseDisclosureReturn;
};

const CandidateCustomPromptModal: React.FC<CandidateCustomPromptModalProps> = ({
  candidateId,
  positionId,
  disclosure,
}) => {
  const taskDefinitionsQuery = useTaskDefinitionsQuery({
    variables: {
      type: TaskDefinitionType.CandidateCustomPrompt,
      positionId,
    },
  });
  const taskDefinitions = taskDefinitionsQuery.data?.taskDefinitions || [];

  const [promptId, setPromptId] = useUrlState({
    key: "promptId",
  });

  // Default to the first task definition
  useEffect(() => {
    if (!promptId && taskDefinitions.length > 0) {
      setPromptId(taskDefinitions[0]?.id);
    }
  }, [promptId, taskDefinitions]);
  const definitionIndex = taskDefinitions.findIndex(
    (def) => def?.id === promptId
  );
  // If definition is specified but not found, default to the first tab
  const tabIndex = definitionIndex < 0 ? 0 : definitionIndex;

  return (
    <Modal
      isOpen={disclosure.isOpen}
      closeOnOverlayClick={false}
      onClose={disclosure.onClose}
      size="3xl"
    >
      <ModalOverlay>
        <ModalContent borderRadius="24px" maxHeight="calc(100dvh - 128px)">
          <ModalHeader
            color="gray.900"
            fontWeight="600"
            fontSize="2xl"
            lineHeight="8"
            pl="6"
            pr="72px"
            pt="6"
            pb="4"
            borderBottom="1px"
            borderBottomColor="gray.100"
          >
            Candidate Summary
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody px="10" pt="0" pb="14" overflow="auto">
            <Tabs
              flex="1"
              mt={4}
              index={tabIndex}
              onChange={(index) => {
                setPromptId(taskDefinitions[index]?.id);
              }}
            >
              <TabList bg="white" pl="8">
                {taskDefinitions.map(
                  (taskDefinition) =>
                    taskDefinition && (
                      <Tab key={taskDefinition.id}>{taskDefinition.name}</Tab>
                    )
                )}
              </TabList>

              <TabPanels>
                {taskDefinitions.map((taskDefinition) => {
                  if (!taskDefinition) {
                    return null;
                  }
                  try {
                    const configuration = JSON.parse(
                      taskDefinition.configuration
                    );
                    return (
                      <TabPanel key={taskDefinition.id}>
                        <CustomPrompt
                          key={taskDefinition.id}
                          taskDefinitionId={taskDefinition.id}
                          defaultPrompt={configuration.user_prompt}
                          candidateId={candidateId}
                          positionId={positionId}
                        />
                      </TabPanel>
                    );
                  } catch (e) {
                    Sentry.captureException(e);
                    return null;
                  }
                })}
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

type CustomPromptProps = {
  taskDefinitionId: string;
  defaultPrompt: string;
  candidateId: string;
  positionId: string | null;
};

const CustomPrompt: React.FC<CustomPromptProps> = ({
  taskDefinitionId,
  defaultPrompt,
  candidateId,
  positionId,
}) => {
  const [prompt, setPrompt] = React.useState(defaultPrompt);
  const [editingPrompt, setEditingPrompt] = React.useState(false);
  const editingEnabled = useFeatureFlag(
    "candidate_custom_prompt:editing_enabled"
  );

  const { runCompleteTask, taskRun, errorMessage, loading } = useCompleteTask(
    taskDefinitionId,
    // Because fields are passed as JSON encoding, we don't get
    // automatic camecase conversion.
    // eslint-disable-next-line camelcase
    { candidate_id: candidateId, position_id: positionId }
  );

  const run = (prompt: string): void => {
    runCompleteTask(
      // eslint-disable-next-line camelcase
      { user_prompt: prompt }
    );
  };

  return (
    <>
      {editingPrompt && (
        <ExpandableTextArea
          mt={10}
          mb={4}
          defaultValue={prompt}
          minHeight="15rem"
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
        />
      )}
      <Flex flexDir="row" mt={10} mb={2}>
        {editingEnabled && !editingPrompt && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditingPrompt(!editingPrompt);
            }}
            display="block"
          >
            Edit Prompt
          </Button>
        )}
        <Button
          ml="auto"
          size="sm"
          onClick={() => {
            run(prompt);
          }}
          disabled={loading}
          display="block"
        >
          Generate Summary
        </Button>
      </Flex>
      <CandidateCustomPromptResult
        taskRun={taskRun}
        errorMessage={errorMessage}
        loading={loading}
      />
    </>
  );
};

export default CandidateCustomPromptModal;
